import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Building2, CheckCheck, Sparkles } from "lucide-react";

/**
 * Agent Labista, replayed.
 *
 * Carried over from the previous site with the animation intact — it was
 * the best thing on that page, and unlike a screenshot it shows the one
 * property that matters: the agent captures the lead progressively, inside
 * the conversation, without ever presenting a form.
 *
 * The exchange is illustrative of the product's behaviour, not a
 * transcript of a real buyer. "Marigold Heights" and "Anita Sharma" are
 * invented for exactly that reason — a real buyer's WhatsApp thread is not
 * ours to publish.
 */

type Side = "in" | "out";

const SCRIPT: { side: Side; text: string }[] = [
  { side: "in", text: "Looking for a 3BHK in Worli, budget around 2.5 cr" },
  {
    side: "out",
    text: "Hi! Marigold Heights has 3BHKs in Worli from ₹2.5 Cr. Possession is Q4 2027. Could I get your name first?",
  },
  { side: "in", text: "Anita Sharma. Timeline: 4 months. Please call me" },
  {
    side: "out",
    text: "Done Anita 🙏 Sharing the brochure now. Sales agent will call in 30 min. Site visit Saturday 11 AM?",
  },
];

export function LabistaChat() {
  const reduce = useReducedMotion();
  // Under reduced motion the whole exchange is present immediately rather
  // than typing itself out — the information is the point, the typing is
  // the flourish.
  const [visible, setVisible] = useState(reduce ? SCRIPT.length : 0);

  // Printing mid-replay would put a half-finished conversation in the
  // brochure, missing the very last message — the one where the visit gets
  // booked, which is the whole point of the exchange.
  useEffect(() => {
    const showAll = () => setVisible(SCRIPT.length);
    window.addEventListener("beforeprint", showAll);
    return () => window.removeEventListener("beforeprint", showAll);
  }, []);

  useEffect(() => {
    if (reduce) {
      setVisible(SCRIPT.length);
      return;
    }
    if (visible >= SCRIPT.length) return;
    const t = setTimeout(() => setVisible((v) => v + 1), visible === 0 ? 600 : 1500);
    return () => clearTimeout(t);
  }, [visible, reduce]);

  return (
    <div className="relative">
      <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[10px] font-medium text-emerald-300">
        via WhatsApp · answered by Labista
      </div>

      <div className="glow-edge print-keep mx-auto w-full max-w-sm rounded-[2rem] border border-white/10 bg-ink-900/80 p-3 shadow-glow backdrop-blur-xl">
        <div className="rounded-[1.6rem] bg-[#0c1428] p-3">
          <div className="flex items-center gap-3 border-b border-white/5 pb-3">
            <div className="grid size-10 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <Building2 className="size-5" />
            </div>
            <div>
              <div className="text-sm font-medium leading-tight">
                Marigold Heights · Sales
              </div>
              <div className="text-[11px] text-emerald-300">Labista · online</div>
            </div>
          </div>

          <div className="min-h-[260px] space-y-2 py-3">
            <AnimatePresence initial={false} mode="popLayout">
              {SCRIPT.slice(0, visible).map((s, idx) => (
                <motion.div
                  key={`${idx}-${s.text.slice(0, 6)}`}
                  layout
                  initial={{ y: 10, opacity: 0, scale: 0.96 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 280, damping: 24 }}
                >
                  <Bubble side={s.side} text={s.text} />
                </motion.div>
              ))}
            </AnimatePresence>

            {visible < SCRIPT.length ? (
              <div className="ml-1 flex items-center gap-1 pt-1 text-white/40">
                <span className="size-1.5 animate-pulseSoft rounded-full bg-white/60" />
                <span className="size-1.5 animate-pulseSoft rounded-full bg-white/60 [animation-delay:120ms]" />
                <span className="size-1.5 animate-pulseSoft rounded-full bg-white/60 [animation-delay:240ms]" />
                <span className="ml-1 text-[10px]">
                  {SCRIPT[visible]?.side === "in"
                    ? "buyer is typing…"
                    : "Labista is typing…"}
                </span>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-3 text-center"
              >
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-emerald-300">
                  <Sparkles className="size-3" />
                  Lead qualified · agent notified
                </div>
                <div className="mt-1 text-xs text-white/70">
                  Anita Sharma · Worli · ₹2.5 Cr · 4 months · call
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* In flow beneath the phone. As an absolutely-positioned float it
       *  sat on top of the "lead qualified" summary — the one frame in the
       *  whole replay that carries the payoff. */}
      <motion.div
        animate={reduce ? undefined : { y: [0, -4, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="mx-auto mt-4 w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.06] p-3 shadow-glow backdrop-blur-xl"
      >
        <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-300">
          Captured automatically
        </div>
        <div className="mt-1 text-xs text-white/80">
          Name · area · budget · timeline · contact mode
        </div>
      </motion.div>
    </div>
  );
}

function Bubble({ side, text }: { side: Side; text: string }) {
  if (side === "in") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/[0.06] px-3 py-2 text-[13px] leading-snug text-white/90">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-emerald-500/90 px-3 py-2 text-[13px] leading-snug text-ink-950 shadow-sm">
        {text}
        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-ink-900/70">
          <span>now</span>
          <CheckCheck className="size-3" />
        </div>
      </div>
    </div>
  );
}
